import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

function MagneticFieldLines() {
  // Dipole field line equation: r = maxR * sin(theta)^2
  const getFieldLinePoints = (lineIdx: number) => {
    const points = [];
    const phi = (lineIdx * Math.PI * 2) / 8; // 8 lines around the equator
    const maxR = 2.4; // width of the field lines
    for (let k = 0; k <= 30; k++) {
      const t = k / 30;
      const theta = t * Math.PI; // 0 to pi
      const r = maxR * Math.sin(theta) * Math.sin(theta);
      const finalR = Math.max(0.6, r); // don't go inside the star core

      const x = finalR * Math.sin(theta) * Math.cos(phi);
      const z = finalR * Math.sin(theta) * Math.sin(phi);
      const y = finalR * Math.cos(theta);

      points.push(new THREE.Vector3(x, y, z));
    }
    return points;
  };

  const curves = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => {
      const pts = getFieldLinePoints(i);
      return new THREE.CatmullRomCurve3(pts);
    });
  }, []);

  return (
    <group>
      {curves.map((curve, idx) => (
        <mesh key={idx}>
          <tubeGeometry args={[curve, 32, 0.012, 8, false]} />
          {/* Alternating gradient colors: cyan and blue */}
          <meshBasicMaterial
            color={idx % 2 === 0 ? '#67E8F9' : '#3B82F6'}
            transparent
            opacity={0.45}
          />
        </mesh>
      ))}
    </group>
  );
}

function BeamParticles({ isNorth }: { isNorth: boolean }) {
  const groupRef = useRef<THREE.Group>(null);
  const count = 30;

  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();
    const drift = elapsed * 1.8; // speed of drifting particles
    if (groupRef.current) {
      groupRef.current.children.forEach((child, j) => {
        const start = 0.6 + (j / count) * 4.0;
        const currentY = 0.6 + ((start - 0.6 + drift) % 4.0);
        child.position.set(0, isNorth ? currentY : -currentY, 0);
      });
    }
  });

  return (
    <group ref={groupRef}>
      {Array.from({ length: count }).map((_, idx) => (
        <mesh key={idx}>
          <sphereGeometry args={[0.022, 8, 8]} />
          <meshBasicMaterial color="#FDE68A" />
        </mesh>
      ))}
    </group>
  );
}

function PulsarSystem() {
  const pulsarGroupRef = useRef<THREE.Group>(null);
  const jetMatRef1 = useRef<THREE.MeshBasicMaterial>(null);
  const jetMatRef2 = useRef<THREE.MeshBasicMaterial>(null);

  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();

    if (pulsarGroupRef.current) {
      // Rapid spin around local Y axis
      pulsarGroupRef.current.rotation.y += 0.05;
      
      // Wobble around Z axis (magnetic offset)
      pulsarGroupRef.current.rotation.z = Math.sin(elapsed * 2.5) * 0.18;
    }

    // Cone opacity pulsing animation
    const op = 0.35 + Math.sin(elapsed * 8.0) * 0.25;
    if (jetMatRef1.current) jetMatRef1.current.opacity = op;
    if (jetMatRef2.current) jetMatRef2.current.opacity = op;
  });

  return (
    <group ref={pulsarGroupRef}>
      {/* Neutron Star Core */}
      <mesh>
        <sphereGeometry args={[0.6, 32, 32]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#60A5FA"
          emissiveIntensity={2.0}
          roughness={0}
          metalness={0.8}
        />
      </mesh>

      {/* Dipole Magnetic Field Lines */}
      <MagneticFieldLines />

      {/* Polar Jet Cone - North */}
      <group position={[0, 2.6, 0]}>
        <mesh>
          <coneGeometry args={[0.15, 4, 16, 1, true]} />
          <meshBasicMaterial
            ref={jetMatRef1}
            color="#FBBF24"
            transparent
            opacity={0.6}
            wireframe={false}
          />
        </mesh>
      </group>

      {/* Polar Jet Cone - South */}
      <group position={[0, -2.6, 0]} rotation={[Math.PI, 0, 0]}>
        <mesh>
          <coneGeometry args={[0.15, 4, 16, 1, true]} />
          <meshBasicMaterial
            ref={jetMatRef2}
            color="#FBBF24"
            transparent
            opacity={0.6}
            wireframe={false}
          />
        </mesh>
      </group>

      {/* Particles along both beams */}
      <BeamParticles isNorth={true} />
      <BeamParticles isNorth={false} />
    </group>
  );
}

export function PulsarMagneticField() {
  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <Canvas camera={{ position: [0, 3, 10], fov: 45 } as any}>
        <color attach="background" args={['#0F172A']} />
        
        <ambientLight intensity={0.1} />
        <pointLight position={[0, 0, 0]} color="#60A5FA" intensity={4.0} decay={1.5} distance={15} />

        <PulsarSystem />
        
        <OrbitControls enableZoom={true} enablePan={false} autoRotate autoRotateSpeed={0.25} />
      </Canvas>

      {/* Info Overlay Box */}
      <div className="absolute top-3 right-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-1 pointer-events-none select-none text-white w-[220px]">
        <span className="text-[9px] text-white/40 tracking-wider uppercase font-bold">Pulsar Dynamics</span>
        <div className="flex flex-col gap-0.5 text-[10px] leading-tight">
          <span className="font-semibold text-cyan-400">Rotation: ~30 rotations/sec</span>
          <span className="text-white/80">Magnetic Axis: Offset from spin axis</span>
          <span className="text-white/80">Beams: Emitted from magnetic poles</span>
        </div>
      </div>

      {/* Title Tag */}
      <div className="absolute top-3 left-3 pointer-events-none select-none text-white/40 text-sm font-medium">
        Pulsar Dipole Magnetic Field & Beams
      </div>
    </div>
  );
}
